/**
 * Shared SSO session helpers for the public site (ERIO ⇄ Ekselens federated
 * Auth0 SSO). The shared-tenant session is signalled by cookies ERIO sets on
 * the parent domain `.ekselens.com`:
 *   ek_sso=1                   presence flag
 *   ek_sso_name=<display name> optional, URL-encoded
 *
 * Until ERIO enables its OIDC endpoints, REACT_APP_ERIO_SSO_ENABLED stays
 * "false" so we don't send users to an unconfigured endpoint (which returns
 * `{"detail":"OIDC SSO not configured"}`). Flip the env to "true" once ERIO
 * ships /api/auth/oidc/login + /logout.
 */
import { useEffect, useState } from "react";

export const SSO_ENABLED =
  String(process.env.REACT_APP_ERIO_SSO_ENABLED || "false").toLowerCase() === "true";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** First-party Auth0 OIDC login on the Ekselens backend (no longer federated
 *  through ERIO — removes ERIO as a single point of failure and lets Ekselens
 *  set the shared `.ekselens.com` cookies itself). */
export const ssoLoginUrl = (returnTo) =>
  `${BACKEND}/api/auth/oidc/login?return_to=${encodeURIComponent(returnTo || `${window.location.origin}/`)}`;

export const ssoLogoutUrl = (returnTo) =>
  `${BACKEND}/api/auth/oidc/logout?return_to=${encodeURIComponent(returnTo || `${window.location.origin}/?signed_out=1`)}`;

// --- ERIO (sister platform) entry points -------------------------------------
// "Open ERIO" links route through ERIO's OIDC login so the session is
// established (or silently resumed via the shared .ekselens.com Auth0 tenant)
// before landing — no raw "not authenticated" bounce.
const ERIO_ORIGIN = "https://erio.ekselens.com";

/** Open ERIO via its OIDC login. Pass `returnTo` to land somewhere specific
 *  after sign-in (omit it to land on ERIO's dashboard). */
export const erioLoginUrl = (returnTo) =>
  `${ERIO_ORIGIN}/api/auth/oidc/login` +
  (returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : "");

/** Deep-link into a specific ERIO path (e.g. "/?source=…") THROUGH the OIDC
 *  login, so campaign-attribution params survive to the post-login landing. */
export const erioDeepLink = (path) => erioLoginUrl(`${ERIO_ORIGIN}${path}`);

export const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
};

export const clearSsoCookies = () => {
  if (typeof document === "undefined") return;
  ["ek_sso", "ek_sso_name", "ek_admin", "ek_tools"].forEach((n) => {
    document.cookie = `${n}=; Max-Age=0; path=/; domain=.ekselens.com`;
    document.cookie = `${n}=; Max-Age=0; path=/`;
  });
};

/** Sign out of the shared session: clear local cookies, then bounce to the
 *  Ekselens backend OIDC logout (terminates the Auth0 session + clears the
 *  shared `.ekselens.com` cookies). */
export const ssoSignOut = () => {
  clearSsoCookies();
  window.location.href = ssoLogoutUrl();
};

/** Reactive read of the shared SSO session from cookies. */
export const useSsoSession = () => {
  const [session, setSession] = useState({ signedIn: false, name: "", isAdmin: false, hasTools: false });
  useEffect(() => {
    if (readCookie("ek_sso") === "1") {
      setSession({
        signedIn: true,
        name: readCookie("ek_sso_name") || "",
        isAdmin: readCookie("ek_admin") === "1",
        hasTools: readCookie("ek_tools") === "1",
      });
    }
  }, []);
  return session;
};
